<template>
  <div class="col-12 connections py-4 mb-4">
    <div class="card finances">
      <div class="card-header">
        <h5 class="card-title mb-0">Финансы</h5>
      </div>

      <div class="d-flex navs">
        <ul class="nav nav-tabs">
          <li>
            <a
              id="analyzesmall-tab"
              :class="['grey-button', { active: hasAnalysisData }]"
              data-bs-toggle="tab"
              href="#tab-analyzesmall"
              role="tab"
              aria-controls="tab-analyzesmall"
              :aria-selected="hasAnalysisData"
              >Анализ</a
            >
          </li>
          <li class="ms-md-4">
            <a
              id="report-tab"
              :class="['grey-button', { active: !hasAnalysisData }]"
              data-bs-toggle="tab"
              href="#tab-report"
              role="tab"
              aria-controls="tab-report"
              :aria-selected="!hasAnalysisData"
              >Бухгалтерская отчетность</a
            >
          </li>
        </ul>
      </div>
      <div class="card-body">
        <div id="myTabContent" class="tab-content">
          <!-- Первый таб -->
          <div
            id="tab-analyzesmall"
            class="tab-pane fade"
            :class="{ 'show active': hasAnalysisData }"
            role="tabpanel"
            aria-labelledby="analyzesmall-tab"
          >
            <template v-if="hasAnalysisData">
              <div class="metrics-grid mt-4">
                <div class="metric-card">
                  <span class="metric-card__label">Выручка</span>
                  <strong class="metric-card__value">{{ formatCompactCurrency(latestRevenue) }}</strong>
                  <span class="metric-card__meta">за {{ lastYear }}</span>
                  <span v-if="revenueDeltaLabel" class="metric-card__trend">{{ revenueDeltaLabel }}</span>
                </div>
                <div class="metric-card">
                  <span class="metric-card__label">Чистая прибыль</span>
                  <strong class="metric-card__value">{{ formatCompactCurrency(latestNetProfit) }}</strong>
                  <span class="metric-card__meta">за {{ lastYear }}</span>
                  <span v-if="profitDeltaLabel" class="metric-card__trend">{{ profitDeltaLabel }}</span>
                </div>
                <div class="metric-card">
                  <span class="metric-card__label">Капитал</span>
                  <strong class="metric-card__value">{{ formatCompactCurrency(latestCapital) }}</strong>
                  <span class="metric-card__meta">на {{ lastYear }} год</span>
                  <span v-if="capitalDeltaLabel" class="metric-card__trend">{{ capitalDeltaLabel }}</span>
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-lg-8 mb-4">
                  <h6 class="finances-title mb-3">Ключевые показатели по годам</h6>
                  <div class="fallback-chart">
                    <BarChart :chart-data="chartData" :chart-options="chartOptions" />
                  </div>
                </div>
                <div class="col-lg-4 mb-4">
                  <div class="card">
                    <div class="card-body">
                      <h6 class="finances-title mb-3">Краткая сводка</h6>
                      <ul class="info-list">
                        <li class="border-bottom py-3">
                          <span class="info-title">Период отчетности</span>
                          <span class="info-value">{{ firstYear }} - {{ lastYear }}</span>
                        </li>
                        <li class="border-bottom py-3">
                          <span class="info-title">Отчетных лет</span>
                          <span class="info-value">{{ chartYears.length }}</span>
                        </li>
                        <li class="py-3">
                          <span class="info-title">Состояние</span>
                          <span class="info-value">{{
                            html ? 'Расширенный анализ доступен' : 'Показываем данные без HTML-анализа'
                          }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-md-5">
                  <div class="card latest-year-card">
                    <div class="card-body">
                      <h6 class="finances-title mb-3">Показатели за {{ lastYear }} год</h6>
                      <ul class="info-list">
                        <li class="border-bottom py-3">
                          <span class="info-title">Выручка</span>
                          <span class="info-bold">{{ formatCompactCurrency(latestRevenue) }}</span>
                        </li>
                        <li class="border-bottom py-3">
                          <span class="info-title">Чистая прибыль</span>
                          <span class="info-bold">{{ formatCompactCurrency(latestNetProfit) }}</span>
                        </li>
                        <li class="border-bottom py-3">
                          <span class="info-title">Капитал и резервы</span>
                          <span class="info-bold">{{ formatCompactCurrency(latestCapital) }}</span>
                        </li>
                        <li class="py-3">
                          <span class="info-title">Активы / пассивы</span>
                          <span class="info-bold"
                            >{{ formatCompactCurrency(latestAssets) }} /
                            {{ formatCompactCurrency(latestLiabilities) }}</span
                          >
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="card latest-year-card">
                    <div class="card-body">
                      <h6 class="finances-title mb-3">Примечание</h6>
                      <p class="mb-0 text-muted">
                        Показатели под графиком приведены к данным за последний доступный отчетный год.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <p v-else class="text-center mt-4 mb-0">Нет данных для финансового анализа</p>
          </div>

          <!-- Второй таб -->
          <div
            id="tab-report"
            class="tab-pane fade"
            :class="{ 'show active': !hasAnalysisData }"
            role="tabpanel"
            aria-labelledby="report-tab"
          >
            <!-- Фильтр и кнопка "Скачать" -->
            <div
              v-if="finances.links && finances.links['Отчет']"
              class="card d-flex flex-row justify-content-between align-items-center col-10 px-4 py-4 mt-4"
            >
              <p class="mb-0">Бухгалтерская отчетность</p>
              <a :href="finances.links['Отчет']['2023']" class="btn-primary btn-small" target="_blank">Скачать</a>
            </div>

            <!-- Бухгалтерский баланс -->
            <h5 class="finances-title mt-4 pt-4 pb-2">Бухгалтерский баланс</h5>
            <div class="card mt-4 inner-card">
              <div class="card-body">
                <div class="row">
                  <div class="col-6">
                    <!-- <BarChart /> -->
                  </div>
                </div>
                <div class="table-container">
                  <table class="table table-bordered colored">
                    <thead>
                      <tr>
                        <th scope="col" class="text-center">Код</th>
                        <th v-for="year in years" :key="year" scope="col" class="text-center">
                          {{ year }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="code in codes" :key="code">
                        <td>{{ code }}</td>
                        <td v-for="year in years" :key="year">
                          {{ getSumOtch(year, code) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import { defineComponent, ref, computed } from 'vue';
  import BarChart from './cards/Diagrams/BarChart.vue';

  export default defineComponent({
    name: 'Finances',
    components: {
      BarChart,
    },
    props: {
      html: String,
      finances: Object,
    },
    setup(props) {
      const isOpen = ref(null);
      const financeYears = computed(() => props.finances?.finances_years || []);
      const hasFinanceYears = computed(() => financeYears.value.length > 0);
      const chartYears = computed(() => {
        const additionalYears = Object.keys(props.finances?.additional_finances || {});
        return additionalYears.length ? additionalYears.sort() : financeYears.value.map((item) => item.year);
      });
      const getMetricValue = (year, code) => {
        return props.finances?.additional_finances?.[year]?.[code]?.СумОтч ?? null;
      };
      const buildMetricSeries = (code) => {
        return chartYears.value.map((year) => {
          const raw = getMetricValue(year, code);
          return typeof raw === 'number' ? raw : null;
        });
      };
      const revenueSeries = computed(() => buildMetricSeries('2110'));
      const netProfitSeries = computed(() => buildMetricSeries('2400'));
      const capitalSeries = computed(() => buildMetricSeries('1300'));
      const assetsSeries = computed(() => buildMetricSeries('1600'));
      const liabilitiesSeries = computed(() => buildMetricSeries('1700'));
      const hasAnalysisData = computed(() => !!props.html || hasFinanceYears.value || chartYears.value.length > 0);
      const firstYear = computed(() => chartYears.value[0] || '—');
      const lastYear = computed(() => chartYears.value[chartYears.value.length - 1] || '—');
      const latestRevenue = computed(() => revenueSeries.value[revenueSeries.value.length - 1] ?? null);
      const latestNetProfit = computed(() => netProfitSeries.value[netProfitSeries.value.length - 1] ?? null);
      const latestCapital = computed(() => capitalSeries.value[capitalSeries.value.length - 1] ?? null);
      const latestAssets = computed(() => assetsSeries.value[assetsSeries.value.length - 1] ?? null);
      const latestLiabilities = computed(() => liabilitiesSeries.value[liabilitiesSeries.value.length - 1] ?? null);
      const buildDeltaLabel = (series) => {
        if (!series || series.length < 2) return '';
        const current = series[series.length - 1];
        const previous = series[series.length - 2];
        if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) return '';
        const percent = ((current - previous) / Math.abs(previous)) * 100;
        const sign = percent > 0 ? '+' : '';
        return `${sign}${percent.toFixed(0)}% к пред. году`;
      };
      const revenueDeltaLabel = computed(() => buildDeltaLabel(revenueSeries.value));
      const profitDeltaLabel = computed(() => buildDeltaLabel(netProfitSeries.value));
      const capitalDeltaLabel = computed(() => buildDeltaLabel(capitalSeries.value));
      const chartData = computed(() => ({
        labels: chartYears.value,
        datasets: [
          {
            label: 'Выручка',
            data: revenueSeries.value,
            backgroundColor: '#86C2FF4D',
            borderColor: '#86C2FF',
            borderWidth: 1,
            borderRadius: 5,
            barPercentage: 0.9,
            categoryPercentage: 0.75,
          },
          {
            label: 'Чистая прибыль',
            data: netProfitSeries.value,
            backgroundColor: '#92D36E66',
            borderColor: '#58B251',
            borderWidth: 1,
            borderRadius: 5,
            barPercentage: 0.9,
            categoryPercentage: 0.75,
          },
          {
            label: 'Капитал',
            data: capitalSeries.value,
            backgroundColor: '#F5C66A66',
            borderColor: '#E6A93C',
            borderWidth: 1,
            borderRadius: 5,
            barPercentage: 0.9,
            categoryPercentage: 0.75,
          },
        ],
      }));

      // Опции для графика
      const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            // position: "top",
          },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return (
                  context.dataset.label +
                  ': ' +
                  new Intl.NumberFormat('ru-RU', {
                    maximumFractionDigits: 0,
                  }).format(context.parsed.y || 0) +
                  ' руб.'
                );
              },
            },
          },
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20,
            left: 20,
            right: 20,
          },
        },
        scales: {
          x: {
            title: {
              display: true,
            },
          },
          y: {
            display: true,
            grid: {
              display: true,
            },
            title: {
              display: false,
            },
            ticks: {
              display: false,
            },
          },
          r: {
            ticks: {
              callback: function () {
                return null;
              },
            },
          },
        },
      };
      const isCollapsed = ref(true);
      const factors = ref([]);
      const financeFactors = ref([]);
      const years = computed(() => {
        if (!props.finances || !props.finances.additional_finances) return [];
        return Object.keys(props.finances.additional_finances);
      });

      const codes = computed(() => {
        if (!props.finances || !props.finances.additional_finances) return [];
        const codesSet = new Set();
        Object.values(props.finances.additional_finances).forEach((yearData) => {
          if (yearData) {
            Object.keys(yearData).forEach((code) => codesSet.add(code));
          }
        });
        return Array.from(codesSet).sort();
      });

      const getSumOtch = (year, code) => {
        if (!props.finances?.additional_finances?.[year]?.[code]) return '—';
        return formatPrice(props.finances.additional_finances[year][code]?.СумОтч);
      };

      function toggleCollapse() {
        isCollapsed.value = !isCollapsed.value;
      }

      const formatPrice = (price) => {
        if (price === null || price === undefined) {
          return '—';
        }
        return new Intl.NumberFormat('ru-RU', {
          style: 'currency',
          currency: 'RUB',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(price);
      };
      const formatCompactCurrency = (value) => {
        if (value === null || value === undefined) {
          return '—';
        }
        const absValue = Math.abs(value);
        if (absValue >= 1_000_000_000) {
          return `${(value / 1_000_000_000).toFixed(1)} млрд руб.`;
        }
        if (absValue >= 1_000_000) {
          return `${(value / 1_000_000).toFixed(1)} млн руб.`;
        }
        if (absValue >= 1_000) {
          return `${(value / 1_000).toFixed(1)} тыс. руб.`;
        }
        return formatPrice(value);
      };

      const getParsing = () => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(props.html, 'text/html');
        const finances = doc.getElementById('finances');
        if (finances) {
          const table = finances.querySelector('table');
          const table1 = finances.querySelectorAll('table')[1];
          const tr = table?.querySelectorAll('tr');
          const tr1 = table1?.querySelectorAll('tr');

          if (tr?.length) {
            tr?.forEach((stroke, index) => {
              const tdFirst = stroke.querySelector('td:nth-child(1)');
              const tdSecond = stroke.querySelector('td:nth-child(2) span');
              if (tdFirst?.textContent && tdFirst?.textContent !== 'Активы') {
                const tableItemObject = {
                  id: index + 1,
                  title: tdFirst.textContent,
                  text: tdSecond.textContent,
                };
                factors.value.push(tableItemObject);
              }
            });

            if (tr1?.length) {
              tr1?.forEach((stroke, index) => {
                const th = stroke.querySelector('th');
                const tdFirst = stroke.querySelector('td:nth-child(1)');
                const tdSecond = stroke.querySelector('td:nth-child(2) span');
                const tableItemObject = {
                  id: index + 1,
                  title: th?.textContent || null,
                  text: tdFirst?.textContent.replace(/\?/g, '') || null,
                  description: tdSecond?.textContent || null,
                };
                financeFactors.value.push(tableItemObject);
              });
            }
          }
        }
      };
      if (props.html) {
        getParsing();
      }

      return {
        isOpen,
        chartData,
        chartOptions,
        factors,
        financeFactors,
        financeYears,
        chartYears,
        hasFinanceYears,
        hasAnalysisData,
        firstYear,
        lastYear,
        latestRevenue,
        latestNetProfit,
        latestCapital,
        latestAssets,
        latestLiabilities,
        revenueDeltaLabel,
        profitDeltaLabel,
        capitalDeltaLabel,
        years,
        codes,
        getSumOtch,
        formatPrice,
        formatCompactCurrency,
        toggleCollapse,
      };
    },
  });
</script>

<style scoped lang="scss">
  .table-container {
    width: 100%;
    height: 500px;
    overflow-x: auto;
    overflow-y: auto;
  }

  .fallback-chart {
    height: 320px;
    padding: 1rem;
    background: #f7f9fc;
    border-radius: 20px;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }

  .metric-card {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(180deg, #fff8e9 0%, #fff3d2 100%);
    border: 1px solid #f4e1a8;
    border-radius: 20px;

    &__label {
      color: #6d7785;
      font-size: 14px;
    }

    &__value {
      color: #1f2d3d;
      font-size: 28px;
      line-height: 1.2;
    }

    &__meta {
      color: #6d7785;
      font-size: 13px;
    }

    &__trend {
      color: #3fa95c;
      font-size: 14px;
      font-weight: 600;
    }
  }

  table {
    width: 100%;

    border-collapse: collapse;

    // min-width: 1000px;

    th {
      position: sticky;
      top: 0;
      z-index: 1;

      background-color: #fff !important;
    }
  }
</style>
